import {
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  resolveModelDisplayName,
  AgentCapabilityResolvedPayloadSchema,
  type AgentCapabilityResolvedPayload,
} from '@reflex/shared';
import { getModelDisplayName } from '@reflex/ui/lib/model-icons';
import type { Attachment } from '@runloop/reflex-contract';
import type { AxonEvent, SandboxProcessSample } from '@reflex/shared';
import { useActiveOrgSlug } from '@reflex/ui/client/context';
import type { SectionNavHandle } from '@reflex/ui/components/layout/SectionNav';
import { ProcessUsageList } from '@reflex/ui/components/sandbox/ProcessUsageList';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@reflex/ui/components/ui/collapsible';
import { ROUTES, withOrgSlug } from '@reflex/shared/routes';
import type {
  AgentConfigItem,
  AgentLifecycleItem,
  AvailableCommand,
  CodexCommandResultItem,
  SetupProgressItem,
  SystemInitBlock,
  TurnBlock,
  UserAttachment,
} from './types.js';
import { parseLaunchedAgents } from './launched-agent.js';
import { CapabilityRequestCard, parseCapabilityRequest } from './CapabilityRequestCard';
import { useLaunchedAgentsEmitter } from './launched-agent-activity.js';
import type { AgentThreadListEntry } from '@reflex/ui/components/agent/AgentThreadList';
import { useSDKStream } from './useSDKStream.js';
import { AssistantTurn } from './AssistantTurn.js';
import { CodexCommandResultBlockView } from './TurnBlockRenderers.js';
import { AnimatedChatItem } from './AnimatedChatItem.js';
import { CHAT_COLUMN_CLASS } from './chat-column.js';
import { ChatTurnNav } from './ChatTurnNav.js';
import type { SessionIndexTurn } from './unified-turn-nav.js';
import { CHAT_END_ANCHOR_ID, CHAT_START_ANCHOR_ID } from './turn-nav.js';
import { useHasRoomForRail } from './use-rail-room.js';
import { useMeasuredRowSizes } from './use-measured-row-sizes.js';
import { useTurnStepping } from './use-turn-stepping.js';
import { extractAuthorIdFromSource, extractPromptViaFromSource } from './timeline-helpers.js';
import {
  groupTranscriptTurns,
  resolveTranscriptWindow,
  shouldRetainVirtualTurnUi,
  INITIAL_VISIBLE_MESSAGES,
  REVEAL_STEP,
  type TranscriptTurnGroup,
} from './transcript-window.js';
import { QueuedMessageBlock } from '../QueuedMessageBubble.js';
import { AgentSetupProgress } from '@reflex/ui/components/bubbles/AgentSetupProgress';
import { WorkingIndicator } from '@reflex/ui/components/content/WorkingIndicator';
import { StackedImages, type StackImage } from '@reflex/ui/components/content/StackedImages';
import { PromptSenderAvatar } from '@reflex/ui/components/shared/PromptSenderAvatar';
import {
  MarkdownContent,
  MarkdownAgentIdProvider,
} from '@reflex/ui/components/content/MarkdownContent';
import { WorkingDirProvider } from '@reflex/ui/components/content/WorkingDirContext';
import { MarkdownWithAgentRefs } from '@reflex/ui/components/content/MarkdownWithAgentRefs';
import {
  parseJsonPayload,
  StructuredPayloadView,
} from '@reflex/ui/components/content/StructuredPayloadView';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { cn } from '@reflex/ui/lib/utils';
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  Bot,
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  CircleStop,
  ClipboardCheck,
  ExternalLink,
  File,
  ArrowLeftRight,
  GitMerge,
  GitPullRequest,
  Loader2,
  MessageSquare,
  Pause,
  Power,
  Server,
  Settings2,
  Workflow,
  X,
} from 'lucide-react';
import { AnimatedCollapse } from './turn-blocks/AnimatedCollapse.js';
import { AgentLogConsole } from './turn-blocks/AgentLogView.js';
import { DividerBubble } from '@reflex/ui/components/stream/DividerBubble';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import { StreamCardBubble } from '@reflex/ui/components/stream/StreamCardBubble';
import { BubbleActionLink } from '@reflex/ui/components/stream/BubbleActionLink';
import { parseEventPayload } from '@reflex/ui/client/event-utils';
import { InlineDocsHint } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { useAutoScroll } from '@reflex/ui/hooks/useAutoScroll';
import { AuthContext } from '@reflex/ui/client/context';
import { useUser } from '@reflex/ui/client/hooks';
import type { QueuedMessage } from '@reflex/ui/client/hooks';
import { useAgentConnectivity } from '@reflex/ui/client/hooks';
import { useTurnMenuVisible } from '@reflex/ui/client/prefs';
import { ConnectivityBanner } from './ConnectivityBanner.js';
import { AskUserOverlayContext, type AskUserOverlayContextValue } from './AskUserOverlayContext.js';
import { CapabilityDockContext, type CapabilityDockContextValue } from './CapabilityDockContext.js';
/**
 * Ambient facts about the agent whose transcript this is: the id a
 * content-scanner component needs to reach its devbox (the filesystem plugin's
 * file-preview popover), and the directory the paths in it are relative to.
 * Both are read far from here — inside markdown, inside a tool row — so they
 * are published once, together, instead of threaded through every renderer.
 */
function TranscriptScope({
  agentId,
  workingDir,
  children,
}: {
  agentId: string;
  workingDir: string | null;
  children: React.ReactNode;
}) {
  return (
    <MarkdownAgentIdProvider agentId={agentId}>
      <WorkingDirProvider workingDir={workingDir}>{children}</WorkingDirProvider>
    </MarkdownAgentIdProvider>
  );
}

/** Keep roughly two viewports of completed turns warm on either side. */
const TURN_VIRTUALIZATION_OVERSCAN_PX = 1_600;

/**
 * How far above the viewport the continuous-scroll sentinel counts as
 * "in contact". Older turns are committed and rendered this far ahead of
 * the reader, so scrolling up lands on content that already exists
 * instead of stopping at the edge for a prepend.
 */
const CONTINUOUS_SCROLL_LOOKAHEAD_PX = 2 * TURN_VIRTUALIZATION_OVERSCAN_PX;

/**
 * An element's layout top measured from the top of the content box:
 * independent of scroll, and of transforms — every row re-runs its enter
 * animation (a translate) when a prepend remounts the transcript, and a
 * bounding rect taken mid-animation would fold that into the correction.
 */
function contentOffset(content: HTMLElement, el: HTMLElement): number {
  let offset = 0;
  for (
    let node: HTMLElement | null = el;
    node && node !== content;
    node = node.offsetParent as HTMLElement | null
  ) {
    offset += node.offsetTop;
  }
  return offset - content.offsetTop;
}

interface VirtualTurnMeasurement {
  height: number;
  width: number;
}

function turnOwnsActiveUi(row: HTMLElement, userInteracted: boolean): boolean {
  return shouldRetainVirtualTurnUi({
    containsActiveElement: row.contains(document.activeElement),
    hasOpenUi:
      row.querySelector('[data-state="open"], details[open], [aria-expanded="true"]') !== null,
    userInteracted,
  });
}

function MeasuredVirtualTurn({
  group,
  rootRef,
  heightCache,
  forceMounted,
  render,
}: {
  group: TranscriptTurnGroup;
  rootRef: RefObject<HTMLElement | null>;
  heightCache: Map<string, VirtualTurnMeasurement>;
  forceMounted: boolean;
  render: () => ReactNode;
}) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const userInteractedRef = useRef(false);
  const [nearViewport, setNearViewport] = useState(true);
  const [widthStale, setWidthStale] = useState(false);
  const mounted = forceMounted || group.mustStayMounted || nearViewport || widthStale;

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!mounted || !row) return;
    // `content-visibility: auto` decides whether a row is relevant only at
    // the next rendering opportunity, so a row measured in the same task it
    // was inserted in (a prepend, a first mount) reads its skipped size —
    // zero, or a stale spacer height after a pane resize, which has no
    // children to reflow. Render the real turn and defeat content-visibility
    // before reading the geometry; the prepend anchor's layout effect runs
    // after this one and needs the true height.
    const forceRender = widthStale || !heightCache.has(group.id);
    if (forceRender) {
      row.style.contentVisibility = 'visible';
      row.style.containIntrinsicSize = '';
      void row.offsetHeight;
    }
    const { height, width } = row.getBoundingClientRect();
    if (height > 0 && width > 0) {
      heightCache.set(group.id, { height, width });
      row.style.containIntrinsicSize = `auto ${height}px`;
    }
    if (forceRender) row.style.contentVisibility = '';
  }, [group.id, heightCache, mounted, widthStale]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const { height, width } = row.getBoundingClientRect();
      const cached = heightCache.get(group.id);
      if (cached && width > 0 && Math.abs(cached.width - width) > 0.5 && !widthStale) {
        setWidthStale(true);
        return;
      }
      if (!mounted) {
        return;
      }
      if (height > 0 && width > 0) {
        heightCache.set(group.id, { height, width });
        row.style.containIntrinsicSize = `auto ${height}px`;
      }
      if (widthStale) setWidthStale(false);
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [group.id, heightCache, mounted, widthStale]);

  useEffect(() => {
    const row = rowRef.current;
    const root = rootRef.current;
    if (!row || !root || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setNearViewport(true);
        } else if (
          heightCache.has(group.id) &&
          !group.mustStayMounted &&
          !forceMounted &&
          !turnOwnsActiveUi(row, userInteractedRef.current)
        ) {
          setNearViewport(false);
        }
      },
      {
        root,
        rootMargin: `${TURN_VIRTUALIZATION_OVERSCAN_PX}px 0px`,
      },
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, [forceMounted, group.id, group.mustStayMounted, heightCache, rootRef]);

  const cachedMeasurement = heightCache.get(group.id);
  const firstEventId = group.eventIds[0];
  return (
    <div
      ref={rowRef}
      id={group.anchorId}
      tabIndex={group.anchorId ? -1 : undefined}
      data-event-id={mounted ? undefined : firstEventId}
      data-testid="virtual-transcript-turn"
      data-virtual-turn=""
      data-virtual-state={mounted ? 'mounted' : 'spacer'}
      data-measured-width={cachedMeasurement?.width}
      onClickCapture={() => {
        userInteractedRef.current = true;
      }}
      onKeyDownCapture={() => {
        userInteractedRef.current = true;
      }}
      className={cn(
        mounted && 'flex flex-col gap-5 empty:hidden',
        group.anchorId && 'scroll-mt-4 outline-none',
      )}
      style={!mounted && cachedMeasurement ? { height: cachedMeasurement.height } : undefined}
    >
      {mounted ? render() : null}
    </div>
  );
}

/**
 * Optimistic user message awaiting echo or in a non-resolved state. The
 * provider owns the canonical list; SDKChatView only needs the subset
 * required to render the bubble + retry / dismiss actions.
 *
 * - `sending` — request in flight; faded bubble + spinner.
 * - `failed` — send rejected by server (or no server response); red
 *   error treatment with Retry / Dismiss.
 * - `unconfirmed` — server acknowledged the send but the agent stream
 *   never echoed the message back; amber warning treatment (the
 *   message was probably delivered, just not confirmed).
 */
export interface PendingUserMessageView {
  clientId: string;
  text: string;
  attachments?: Array<{ name?: string; mimeType?: string; data?: string }>;
  status: 'sending' | 'unconfirmed' | 'failed';
  error?: string;
  /**
   * When true, this entry is derived from `agent.prompt` rather than a
   * real user send — the optimistic bubble shown while a fresh agent is
   * booting. Synthetic entries don't expose Retry / Dismiss actions
   * because there's no underlying request to retry or queue entry to
   * dismiss; the agent will resolve them by echoing the prompt.
   */
  synthetic?: boolean;
  /**
   * Id of the user to attribute this bubble to. Carried on synthetic
   * entries so a booting-agent preview renders the launcher's avatar /
   * name even when the viewer is someone else. Omitted for real sends —
   * those default to the current viewer.
   */
  userId?: string;
}

interface SDKChatViewProps {
  events: AxonEvent[];
  /**
   * Whether the agent is actively working (status `running`/`starting`).
   * Used to show a "waiting for output" indicator after the user sends a
   * message but before any assistant blocks have arrived.
   */
  isGenerating?: boolean;
  /** Shared with the host navigation when only background work remains. */
  workingLabel?: string;
  agentId: string;
  /**
   * Model the run was launched with. Used only as the fallback label: a turn
   * that named its own model wins, because a mid-session `/model` switch
   * never reaches the agent record (see `sdk-chat/active-model.ts`).
   */
  agentModel?: string | null;
  protocol?: 'claude-code' | 'opencode' | 'codex' | null;
  onCommandsChange?: (commands: AvailableCommand[]) => void;
  /**
   * Called with the model the stream reports as currently answering — null
   * until a session announces one. Lets the composer's model chip follow a
   * mid-session switch instead of showing the launch-time model forever.
   */
  onActiveModelChange?: (model: string | null) => void;
  /**
   * Called with the agents this session launched through Reflex MCP, in the
   * shared thread-row shape and newest launch first, whenever the parsed set
   * changes. Lets the host surface them in the composer's activity bar
   * alongside the transcript's cards. Emitted pre-mapped so the UI-only card
   * type stays inside plugin-chat (see `launched-agent.ts`); each entry's
   * `id` is the child agent's id, so a row click can open that agent.
   */
  onLaunchedAgentsChange?: (agents: AgentThreadListEntry[]) => void;
  queuedMessages?: QueuedMessage[];
  /**
   * Edit a queued message. `attachments`, when passed, replaces the stored
   * list — that's how the queue row removes a single attachment.
   */
  onEditQueued?: (id: string, text: string, attachments?: Attachment[]) => void;
  onDeleteQueued?: (id: string) => void;
  onReorderQueued?: (orderedIds: string[]) => void;
  /**
   * Send a queued message now, ahead of the queue, by redirecting the running
   * agent. Optional: only wired when the active provider can redirect.
   */
  onSendQueuedNow?: (id: string) => void;
  /**
   * Optimistic user messages awaiting echo from the live stream. Each
   * renders as a faded user bubble with a spinner (or a failed state
   * with Retry / Dismiss actions).
   */
  pendingMessages?: PendingUserMessageView[];
  onRetryPending?: (clientId: string) => void;
  onDismissPending?: (clientId: string) => void;
  /**
   * When provided, the chat will scroll to and flash-highlight the message or
   * block whose `sourceEvent.id` matches. Used for deep-linking from the Axon
   * event viewer back to the corresponding chat message.
   */
  highlightEventId?: string;
  /**
   * Called after the flash-highlight has been applied, so the parent can
   * clear any URL state that drove it.
   */
  onHighlightConsumed?: () => void;
  /**
   * Fires when an AskUserQuestion prompt starts or stops awaiting a reply.
   * The host uses this to disable the composer while the lifted question
   * overlay is showing, so the user answers the prompt rather than typing
   * past it.
   */
  onActiveQuestionChange?: (pending: boolean) => void;
  /**
   * Bind the document-global `alt+↑/↓` turn stepping. Set only by the live
   * agent surface: a page can embed several replays (marketing blocks, docs),
   * and each would otherwise bind its own competing listener.
   */
  globalHotkeys?: boolean;
  /**
   * Paged-history controls for an indexed agent whose transcript loaded
   * last-turn-first: the load-earlier affordance and the session-index
   * turns below the loaded window, which the rail lists and jumps to.
   * Absent for legacy agents — the view behaves exactly as before paging.
   */
  pagedHistory?: PagedHistoryControls;
}

/** See {@link SDKChatViewProps.pagedHistory}. */
export interface PagedHistoryControls {
  /** History exists below the loaded window. */
  hasMore: boolean;
  /** A load-earlier fetch is in flight. */
  isLoadingEarlier: boolean;
  onLoadEarlier: () => void;
  /**
   * The session index's prompt rows for the whole session, in session
   * order. The turn rail merges them with the loaded turns into one
   * continuously numbered list (see `buildUnifiedTurnRail`).
   */
  indexTurns: SessionIndexTurn[];
  /** Load pages until the turn at `seqNumber` is in memory, then scroll to it. */
  onJumpToTurn: (seqNumber: number) => void;
  /** The turn a jump is currently loading toward, for the rail's spinner. */
  jumpPendingSeq: number | null;
  /**
   * Continuous scroll: prepend the prefetched previous turn (staged by the
   * data layer's turn pager) into the transcript. `committed` — a turn
   * landed. `pending` — the turn's fetch is already in flight and the
   * pager commits it on arrival; the view must wait, not page-back, or two
   * overlapping history fetches run at once. `miss` — empty or stale
   * buffer with nothing in flight, the fast-scroll case — makes the view
   * fall back to `onLoadEarlier`'s bigger page. Absent when no pager runs;
   * the load-earlier pill is then the only affordance, exactly as before
   * continuous scroll existed.
   */
  commitStagedTurn?: () => 'committed' | 'pending' | 'miss';
}

function UserMessage({
  content,
  attachments,
  sourceEvent,
  isPending,
  isFailed,
  isUnconfirmed,
  errorMessage,
  senderUserId,
  onRetry,
  onDismiss,
}: {
  content: string;
  attachments?: UserAttachment[];
  sourceEvent?: AxonEvent;
  isPending?: boolean;
  isFailed?: boolean;
  isUnconfirmed?: boolean;
  errorMessage?: string;
  /**
   * When set on an optimistic bubble, attribute to this user instead of
   * the current viewer. Used by the synthetic initial-prompt preview so
   * an agent launched by someone else shows the launcher, not whoever
   * happens to be looking at the chat.
   */
  senderUserId?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}) {
  const auth = useContext(AuthContext);
  const authUser = auth?.user ?? null;
  const isOptimistic = isPending || isFailed || isUnconfirmed;

  // Optimistic bubbles usually haven't been echoed by the server yet,
  // so the event has no source. If the caller supplied a `senderUserId`
  // (e.g. the synthetic preview bubble carries the agent's launcher),
  // fetch and attribute to that user. Otherwise the sender is the
  // current viewer. Real bubbles read the author from the event source.
  const authorIdFromSource = extractAuthorIdFromSource(sourceEvent?.source);
  // Who relayed it, if anyone. Optimistic bubbles are always the viewer
  // typing into this pane, so only a server-echoed source can carry a relay.
  const via = isOptimistic ? null : extractPromptViaFromSource(sourceEvent?.source);
  const authorIdToFetch = isOptimistic ? (senderUserId ?? null) : authorIdFromSource;
  const { data: fetchedAuthor } = useUser(authorIdToFetch ?? undefined);

  let userId: string | undefined;
  let userName: string | undefined;
  let avatarUrl: string | undefined;
  let isUnknown = false;
  if (isOptimistic) {
    if (senderUserId) {
      // Prefer the resolved user record when available; while the fetch
      // is in flight, render with just the id so the avatar shows a
      // stable placeholder rather than briefly flashing the viewer.
      userId = fetchedAuthor?.id ?? senderUserId;
      userName = fetchedAuthor ? (fetchedAuthor.name ?? fetchedAuthor.login) : undefined;
      avatarUrl = fetchedAuthor?.avatarUrl ?? undefined;
    } else {
      userId = authUser?.id;
      userName = authUser ? (authUser.name ?? authUser.login) : undefined;
      avatarUrl = authUser?.avatarUrl ?? undefined;
    }
  } else if (fetchedAuthor) {
    userId = fetchedAuthor.id;
    userName = fetchedAuthor.name ?? fetchedAuthor.login;
    avatarUrl = fetchedAuthor.avatarUrl ?? undefined;
  } else {
    // Legacy / Slack-without-mapping / lookup-not-yet-resolved →
    // explicit "Unknown user" rather than silently misattributing.
    isUnknown = true;
    userName = 'Unknown user';
  }

  // Failed bubbles fade more strongly than unconfirmed ones — failed
  // means the message likely didn't go through, while unconfirmed
  // means it probably did.
  const opacityClass = isFailed
    ? 'opacity-60'
    : isUnconfirmed
      ? 'opacity-80'
      : isPending
        ? 'opacity-60'
        : '';

  return (
    <div
      className="group/msg"
      data-event-id={sourceEvent?.id}
      data-pending={isPending ? 'true' : undefined}
      data-failed={isFailed ? 'true' : undefined}
      data-unconfirmed={isUnconfirmed ? 'true' : undefined}
      data-author-id={userId ?? undefined}
      data-author-unknown={isUnknown ? 'true' : undefined}
      data-prompt-via={via ?? undefined}
      data-testid={userId ? `user-message-author-${userId}` : 'user-message-author-unknown'}
    >
      <div className={cn('flex items-start gap-3', opacityClass)}>
        <div className="mt-0.5 shrink-0">
          <PromptSenderAvatar via={via} userId={userId} userName={userName} avatarUrl={avatarUrl} />
        </div>
        <div className="relative min-w-0 flex-1 pt-0.5">
          {attachments && attachments.length > 0 && (
            <div className="mb-2">
              {(() => {
                const images = attachments
                  .filter(
                    (att): att is (typeof attachments)[0] & { type: 'image' } =>
                      att.type === 'image' && !!att.data && !!att.mimeType,
                  )
                  .map(
                    (att) =>
                      ({
                        src: `data:${att.mimeType};base64,${att.data}`,
                        alt: att.name ?? 'attachment',
                      }) as StackImage,
                  );

                const files = attachments.filter((att) => att.type === 'file');

                return (
                  <>
                    {images.length > 0 && (
                      <div className="mb-2">
                        <StackedImages images={images} cardWidth={200} cardHeight={125} />
                      </div>
                    )}
                    {files.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {files.map((att, i) => (
                          <span
                            key={i}
                            className="inline-flex max-w-[280px] items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-foreground"
                          >
                            <File className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="truncate">{att.name ?? 'file'}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          {content && (
            <div className="flex items-start gap-2">
              <div
                className={cn('min-w-0 flex-1', sourceEvent && !isOptimistic && 'pr-5')}
                data-testid="user-message-content"
              >
                <MarkdownWithAgentRefs content={content} size="sm" preserveLineBreaks />
              </div>
              {isPending && (
                <Spinner size="xs" className="mt-1 text-muted-foreground" aria-label="Sending..." />
              )}
            </div>
          )}
          {isFailed && (
            <div className="mt-1.5 flex items-center gap-2">
              <AlertTriangle className="h-3 w-3 shrink-0 text-status-error" />
              <span className="text-2xs text-destructive">{errorMessage ?? 'Failed to send.'}</span>
              {onRetry && (
                <Button
                  variant="link"
                  onClick={onRetry}
                  className="h-auto p-0 text-2xs font-medium underline-offset-2"
                  aria-label="Retry sending message"
                >
                  Retry
                </Button>
              )}
              {onDismiss && (
                <Button
                  variant="ghost"
                  onClick={onDismiss}
                  className="h-auto gap-0.5 p-0 text-2xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:bg-transparent"
                  aria-label="Dismiss failed message"
                >
                  <X className="size-3" />
                  Dismiss
                </Button>
              )}
            </div>
          )}
          {isUnconfirmed && !isFailed && (
            <div className="mt-1.5 flex items-center gap-2">
              <CircleAlert className="h-3 w-3 shrink-0 text-status-warning" />
              <span className="text-2xs text-status-warning">
                {errorMessage ?? 'Sent — awaiting confirmation.'}
              </span>
              {onRetry && (
                <Button
                  variant="link"
                  onClick={onRetry}
                  className="h-auto p-0 text-2xs font-medium underline-offset-2"
                  aria-label="Resend message"
                >
                  Resend
                </Button>
              )}
              {onDismiss && (
                <Button
                  variant="ghost"
                  onClick={onDismiss}
                  className="h-auto gap-0.5 p-0 text-2xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:bg-transparent"
                  aria-label="Dismiss unconfirmed message"
                >
                  <X className="size-3" />
                  Dismiss
                </Button>
              )}
            </div>
          )}
          {sourceEvent && !isOptimistic && (
            <div className="pointer-events-none absolute right-0 top-0 opacity-0 transition-opacity group-hover/msg:pointer-events-auto group-hover/msg:opacity-100">
              <EventDetailTooltip event={sourceEvent} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentConfigBanner({ item }: { item: AgentConfigItem }) {
  const { config } = item;
  const label = config.blueprintName ?? config.agentBinary ?? config.agentType;
  const modelLabel = config.model ? ` · ${resolveModelDisplayName(config.model)}` : '';

  return (
    <div data-event-id={item.sourceEvent?.id}>
      <DividerBubble event={item.sourceEvent} className="py-1">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-border/40 bg-muted/20 px-3 py-1 text-xs text-muted-foreground">
          <Settings2 className="h-3 w-3" />
          <span className="font-medium">{label}</span>
          {modelLabel && <span>{modelLabel}</span>}
        </div>
      </DividerBubble>
    </div>
  );
}

const LIFECYCLE_META: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  'agent.interrupted': { icon: Bot, color: 'text-status-accent-violet' },
  'agent.stopped': { icon: Bot, color: 'text-status-accent-violet' },
  'agent.killed': { icon: Bot, color: 'text-status-accent-violet' },
  'agent.complete': { icon: Bot, color: 'text-status-accent-violet' },
  'agent.error': { icon: AlertTriangle, color: 'text-status-error' },
  'devbox.running': { icon: Server, color: 'text-status-success' },
  'devbox.suspended': { icon: Pause, color: 'text-status-accent-amber' },
  'devbox.shutdown': { icon: Power, color: 'text-muted-foreground' },
  'devbox.failed': { icon: AlertTriangle, color: 'text-status-error' },
  'agent.runtime_settings_changed': { icon: ArrowLeftRight, color: 'text-muted-foreground' },
};

const RUNLOOP_TRIAL_DEVBOX_LIMIT_PATTERN =
  /\b429\b[\s\S]*\btrial account limit reached\b[\s\S]*\bdevboxes?\b/i;

function isRunloopTrialLimitError(errorMessage: string): boolean {
  // A 429 alone is ambiguous (models, proxies, and Reflex itself may emit
  // one). Show plan guidance only for the account-limit/devbox signature
  // returned by Runloop trial accounts.
  return RUNLOOP_TRIAL_DEVBOX_LIMIT_PATTERN.test(errorMessage);
}

function AgentErrorCard({ item }: { item: AgentLifecycleItem }) {
  const slug = useActiveOrgSlug();
  const planPath = withOrgSlug(slug, '/configure/usage/plan');
  const errorMessage = item.errorMessage ?? 'An unknown error occurred.';
  const logs = item.logs?.trim();
  const showTrialLimitNotice = isRunloopTrialLimitError(errorMessage);
  return (
    <div data-event-id={item.sourceEvent?.id}>
      <StreamCardBubble
        icon={<AlertTriangle className="h-4 w-4 text-status-error" />}
        color="red"
        title={item.title ?? 'Error'}
        timestamp={item.timestamp}
        event={item.sourceEvent}
      >
        <MarkdownContent content={errorMessage} size="xs" className="mt-1" />
        {showTrialLimitNotice && (
          <div className="mt-3 border-t border-status-error/20 pt-3">
            <p className="text-xs text-muted-foreground">
              Trial accounts can run up to three devboxes at a time across the Runloop account. Shut
              down a devbox, then retry, or upgrade the plan to run more.
            </p>
            <Link
              to={planPath}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-status-error/30 bg-status-error/10 px-3 py-1.5 text-xs font-medium text-status-error transition-colors hover:bg-status-error/20"
            >
              View plan and billing
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        )}
        {logs && (
          <details open className="mt-2" data-testid="agent-error-logs">
            <summary className="cursor-pointer select-none text-2xs font-medium text-muted-foreground hover:text-foreground">
              Failure logs
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/50 p-2 font-mono text-2xs leading-relaxed text-muted-foreground">
              {logs}
            </pre>
          </details>
        )}
      </StreamCardBubble>
    </div>
  );
}

function FlowSourceCard({ item }: { item: AgentLifecycleItem }) {
  const slug = useActiveOrgSlug();
  const payload = (item.sourceEvent?.payload ?? null) as {
    flowName?: string;
    flowRunId?: string;
    nodeId?: string;
    nodeName?: string;
    nodeType?: string;
  } | null;
  const flowName = payload?.flowName ?? 'flow';
  const deepLink =
    payload?.flowRunId && payload?.nodeId
      ? ROUTES.org(slug).flowRunNode(payload.flowRunId, payload.nodeId)
      : null;

  return (
    <div data-event-id={item.sourceEvent?.id}>
      <StreamCardBubble
        icon={<Workflow className="h-4 w-4" />}
        color="indigo"
        title={`Started from automation: ${flowName}`}
        timestamp={item.timestamp}
        event={item.sourceEvent}
      >
        {payload?.nodeName && (
          <p className="mt-1 text-xs text-muted-foreground">
            Node: <span className="font-medium text-foreground">{payload.nodeName}</span>
            {payload.nodeType && (
              <span className="ml-1.5 rounded bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono">
                {payload.nodeType}
              </span>
            )}
          </p>
        )}
        {deepLink && (
          <Link
            to={deepLink}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-status-accent-indigo/30 bg-status-accent-indigo/10 px-3 py-1.5 text-xs font-medium text-status-accent-indigo transition-colors hover:bg-status-accent-indigo/20"
          >
            Open flow run
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </StreamCardBubble>
    </div>
  );
}

function FlowResultCard({ item }: { item: AgentLifecycleItem }) {
  const slug = useActiveOrgSlug();
  const payload = (item.sourceEvent?.payload ?? null) as {
    flowName?: string;
    flowRunId?: string;
    nodeId?: string;
    nodeName?: string;
    summary?: string | null;
    structuredOutput?: Record<string, unknown> | null;
  } | null;
  const deepLink =
    payload?.flowRunId && payload?.nodeId
      ? ROUTES.org(slug).flowRunNode(payload.flowRunId, payload.nodeId)
      : null;

  const hasStructuredOutput =
    payload?.structuredOutput &&
    typeof payload.structuredOutput === 'object' &&
    Object.keys(payload.structuredOutput).length > 0;
  // Agents sometimes return their structured output as the summary string
  // itself (a raw JSON blob). Render it as a structured payload instead of
  // dumping the JSON through markdown; when the parsed summary duplicates
  // `structuredOutput`, drop it entirely.
  const summaryJson = payload?.summary ? parseJsonPayload(payload.summary) : null;
  const summaryText = summaryJson ? null : payload?.summary;
  const structuredValue = hasStructuredOutput ? payload?.structuredOutput : summaryJson;

  return (
    <div data-event-id={item.sourceEvent?.id}>
      <StreamCardBubble
        icon={<CheckCircle2 className="h-4 w-4" />}
        color="teal"
        title="Returned to automation"
        timestamp={item.timestamp}
        event={item.sourceEvent}
      >
        {payload?.nodeName && (
          <p className="mt-1 text-xs text-muted-foreground">
            Result handed back to{' '}
            <span className="font-medium text-foreground">{payload.nodeName}</span>
            {payload.flowName && (
              <>
                {' '}
                in <span className="font-medium text-foreground">{payload.flowName}</span>
              </>
            )}
          </p>
        )}
        {summaryText && (
          <div className="mt-2">
            <MarkdownContent content={summaryText} size="xs" />
          </div>
        )}
        {structuredValue ? (
          <div className="mt-2">
            <StructuredPayloadView value={structuredValue} compact />
          </div>
        ) : null}
        {deepLink && (
          <Link
            to={deepLink}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-status-accent-teal/30 bg-status-accent-teal/10 px-3 py-1.5 text-xs font-medium text-status-accent-teal transition-colors hover:bg-status-accent-teal/20"
          >
            Open flow run
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </StreamCardBubble>
    </div>
  );
}

/**
 * Canonical render for `agent.daemon_started`. This lives in plugin-chat
 * (not as a web stream bubble) because the agent conversation is owned by
 * SDKChatView and plugins can't import from `web/`. The generic
 * `EventBubble` only renders event-type + text and does NOT delegate here,
 * so this card is the one place the clickable "Open in browser" affordance
 * is wired — keep daemon rendering here rather than re-introducing a
 * parallel web bubble. The `url` is built server-side from the agent's
 * tunnel key + port (see SandboxBridgeService), so the client just links it.
 */
function DaemonStartedCard({ item }: { item: AgentLifecycleItem }) {
  const payload = (item.sourceEvent?.payload ?? null) as {
    name?: string;
    port?: number;
    url?: string;
    info?: string;
  } | null;
  const name = payload?.name ?? 'daemon';
  const port = payload?.port;
  const info = payload?.info;

  return (
    <div data-event-id={item.sourceEvent?.id}>
      <StreamCardBubble
        icon={
          <span className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-status-info/25 bg-gradient-to-br from-status-info/15 to-status-accent-teal/10 text-status-info shadow-sm">
            <Server className="h-4 w-4" />
            <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-success/70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-status-success ring-2 ring-card" />
            </span>
          </span>
        }
        color="blue"
        rawIcon
        timestamp={item.timestamp}
        event={item.sourceEvent}
        data-testid="daemon-started-card"
      >
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-status-info/80">
            Daemon started
          </p>
          <p
            className="mt-0.5 text-sm font-semibold leading-snug text-foreground"
            data-testid="daemon-started-name"
          >
            {name}
          </p>
          {port || info ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-relaxed text-muted-foreground">
              {port ? (
                <span className="inline-flex items-center gap-1.5">
                  Listening on
                  <span className="rounded-md border border-border/60 bg-muted/60 px-1.5 py-0.5 font-medium tabular-nums text-foreground">
                    :{port}
                  </span>
                </span>
              ) : null}
              {port && info ? (
                <span aria-hidden className="text-muted-foreground/40">
                  ·
                </span>
              ) : null}
              {info ? <span>{info}</span> : null}
            </div>
          ) : null}
        </div>
        {payload?.url && (
          <a
            href={payload.url}
            target="_blank"
            rel="noreferrer"
            data-testid="daemon-open-in-browser"
            className="group mt-3 inline-flex items-center gap-1.5 rounded-md border border-status-info/30 bg-status-info/10 px-3 py-1.5 text-xs font-medium text-status-info shadow-sm transition-all hover:border-status-info/50 hover:bg-status-info/20 hover:shadow"
          >
            Open in browser
            <ExternalLink className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        )}
      </StreamCardBubble>
    </div>
  );
}

interface WorkAssessmentPayload {
  title?: string;
  summary?: string;
  target?: { kind?: string; label?: string; url?: string };
}

/**
 * Canonical render for `agent.work_assessment` — the announcement card for a
 * structured walkthrough the agent published via `reflex-service assessment`.
 *
 * Like the daemon and PR cards, this lives in plugin-chat because the agent
 * conversation is owned by SDKChatView and plugins cannot import from `web/`.
 * It reads only the host envelope (title, summary, target), never the
 * document: the document's schema belongs to
 * `@reflex/plugin-work-assessment`, and plugin-chat depending on that package
 * to render a card would couple the conversation surface to one plugin's
 * domain model.
 *
 * "Open assessment" asks the host to switch to the Assessment tab and select
 * this assessment. The axon event id *is* the assessment's identity, so the
 * request carries it as `eventId` and the host routes it through the same
 * `highlightEventId` channel every plugin tab already receives.
 */
function WorkAssessmentCard({ item, agentId }: { item: AgentLifecycleItem; agentId: string }) {
  const payload = parseEventPayload<WorkAssessmentPayload>(item.sourceEvent?.payload) ?? {};
  const eventId = item.sourceEvent?.id;

  const openAssessment = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('reflex:request-agent-tab', {
        detail: { tabId: 'work-assessment', agentId, eventId },
      }),
    );
  }, [agentId, eventId]);

  return (
    <div data-event-id={eventId}>
      <StreamCardBubble
        icon={<ClipboardCheck className="h-4 w-4" />}
        color="violet"
        title="Work assessment"
        timestamp={item.timestamp}
        event={item.sourceEvent}
        data-testid="work-assessment-card"
      >
        <p
          className="mt-1 text-sm font-medium text-foreground"
          data-testid="work-assessment-card-title"
        >
          {payload.title ?? 'Assessment'}
        </p>
        {payload.summary ? (
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{payload.summary}</p>
        ) : null}
        {payload.target?.label ? (
          <p className="mt-1.5 text-xs text-muted-foreground/80">{payload.target.label}</p>
        ) : null}
        <button
          type="button"
          onClick={openAssessment}
          data-testid="work-assessment-open"
          className="group mt-3 inline-flex items-center gap-1.5 rounded-md border border-status-accent-violet/30 bg-status-accent-violet/10 px-3 py-1.5 text-xs font-medium text-status-accent-violet shadow-sm transition-all hover:border-status-accent-violet/50 hover:bg-status-accent-violet/20 hover:shadow"
        >
          Open assessment
          <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </button>
      </StreamCardBubble>
    </div>
  );
}

interface PrEventPayload {
  url?: string;
  number?: number;
  title?: string;
  repo?: string;
  state?: string;
  reviewer?: string;
  checkName?: string;
  conclusion?: string;
}

/**
 * Canonical render for the PR lifecycle events (`agent.pr_created` /
 * `_merged` / `_closed` / `_checks_failed` / `_review`). Like
 * `DaemonStartedCard`, this lives in plugin-chat because the agent
 * conversation is owned by SDKChatView and plugins can't import from `web/`.
 * One card handles every PR event; icon, color, and heading vary by type
 * (and, for reviews, by approve vs changes-requested).
 */
function AgentPrEventCard({ item }: { item: AgentLifecycleItem }) {
  const payload = parseEventPayload<PrEventPayload>(item.sourceEvent?.payload) ?? {};
  const eventType = item.eventType;

  let Icon = GitPullRequest;
  let color: 'amber' | 'red' | 'blue' | 'indigo' | 'teal' | 'purple' | 'violet' = 'indigo';
  let linkColor: 'indigo' | 'purple' | 'blue' | 'teal' | 'amber' = 'indigo';
  let heading = 'Pull request';

  if (eventType === 'agent.pr_created') {
    heading = 'Pull request opened';
  } else if (eventType === 'agent.pr_merged') {
    Icon = GitMerge;
    color = 'purple';
    linkColor = 'purple';
    heading = 'Pull request merged';
  } else if (eventType === 'agent.pr_closed') {
    color = 'red';
    heading = 'Pull request closed';
  } else if (eventType === 'agent.pr_checks_failed') {
    Icon = AlertTriangle;
    color = 'red';
    linkColor = 'amber';
    heading = 'Checks failed';
  } else if (eventType === 'agent.pr_review') {
    if (payload.state === 'changes_requested') {
      Icon = CircleAlert;
      color = 'amber';
      linkColor = 'amber';
      heading = 'Review: changes requested';
    } else if (payload.state === 'commented') {
      Icon = MessageSquare;
      color = 'blue';
      linkColor = 'blue';
      heading = 'Review: commented';
    } else {
      Icon = CheckCircle2;
      color = 'teal';
      linkColor = 'teal';
      heading = 'Review: approved';
    }
  }

  const { number, title, repo, url } = payload;
  const detail =
    eventType === 'agent.pr_checks_failed'
      ? [payload.checkName, payload.conclusion].filter(Boolean).join(' · ')
      : eventType === 'agent.pr_review' && payload.reviewer
        ? `by ${payload.reviewer}`
        : repo;

  return (
    <div data-event-id={item.sourceEvent?.id}>
      <StreamCardBubble
        icon={<Icon className="h-4 w-4" />}
        color={color}
        title={heading}
        timestamp={item.timestamp}
        event={item.sourceEvent}
        data-testid={`pr-event-card-${eventType}`}
      >
        {number != null || title ? (
          <p className="mt-1 text-sm font-medium text-foreground">
            {number != null ? <span className="text-muted-foreground">#{number} </span> : null}
            {title ?? repo ?? 'Pull request'}
          </p>
        ) : null}
        {detail ? <p className="mt-1 text-xs text-muted-foreground">{detail}</p> : null}
        {url ? (
          <BubbleActionLink color={linkColor} href={url}>
            View on GitHub
            <ExternalLink className="h-3 w-3" />
          </BubbleActionLink>
        ) : null}
      </StreamCardBubble>
    </div>
  );
}

// 24-hour HH:MM:SS, matching the monitor panel's axis ticks (formatBucketShort)
// so the inline mini-graph reads the same as the live charts.
function formatClockTime(ms: number): string {
  const d = new Date(ms);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

/**
 * Static mini-chart of a performance warning's detection window. Drawn from the
 * `series` embedded in the event payload (percent values, oldest -> newest), so
 * it renders identically whether the warning is live or replayed long after the
 * sandbox is gone. Carries its own axes: a percent y-axis (with the threshold
 * line labelled) and an absolute wall-clock x-axis anchored to the event's own
 * timestamp, so a reader sees when the spike actually happened rather than its
 * position relative to nearby chat events.
 */
function WarningSparkline({
  series,
  threshold,
  windowSeconds,
  endTime,
}: {
  series: number[];
  threshold?: number;
  windowSeconds?: number;
  /** Epoch ms the window ended (when the warning tripped) - the event time. */
  endTime: number;
}): React.ReactElement | null {
  if (series.length < 2) return null;
  const width = 240;
  const height = 48;
  const padY = 4;
  // cpu/memory are percentages; fix the domain to 0..100 so the threshold line
  // and the curve share the same scale the live charts use.
  const domainMax = 100;
  const innerH = height - padY * 2;
  const stepX = width / (series.length - 1);
  const toX = (i: number) => i * stepX;
  const toY = (v: number) => padY + (1 - Math.min(Math.max(v, 0), domainMax) / domainMax) * innerH;
  const points = series.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');
  const areaPath = `M0,${height} L${points.split(' ').join(' L')} L${toX(
    series.length - 1,
  )},${height} Z`;
  const thresholdY = threshold !== undefined ? toY(threshold) : undefined;
  const peak = Math.round(Math.max(...series));
  // Anchor the x-axis to real clock time. The event's own timestamp is when the
  // warning tripped (the newest sample); the window started `windowSeconds`
  // earlier. Both are persisted, so this reads correctly on later replay.
  const spanMs = windowSeconds && windowSeconds > 0 ? windowSeconds * 1000 : 0;
  const startLabel = formatClockTime(endTime - spanMs);
  const endLabel = formatClockTime(endTime);

  return (
    <div className="mt-2" data-testid="performance-warning-chart">
      <div className="flex gap-1.5">
        {/* y-axis units: cpu/memory are percentages of the box's limit */}
        <div className="flex h-12 w-9 shrink-0 flex-col justify-between py-px text-right text-[11px] leading-none tabular-nums text-muted-foreground">
          <span>100%</span>
          <span>0%</span>
        </div>
        <div className="relative min-w-0 flex-1">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            className="h-12 w-full text-status-warning"
            role="img"
            aria-label={`${
              threshold !== undefined
                ? `Usage versus the ${Math.round(threshold)}% threshold`
                : 'Usage'
            } from ${startLabel} to ${endLabel}, peaking at ${peak}%`}
            data-testid="performance-warning-sparkline"
          >
            <path d={areaPath} fill="currentColor" fillOpacity={0.12} stroke="none" />
            {thresholdY !== undefined ? (
              <line
                x1={0}
                x2={width}
                y1={thresholdY}
                y2={thresholdY}
                stroke="currentColor"
                strokeOpacity={0.4}
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            ) : null}
            <polyline
              points={points}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {thresholdY !== undefined ? (
            <span
              className="absolute left-1 -translate-y-1/2 rounded-sm bg-background/80 px-1 text-[10px] leading-none tabular-nums text-status-warning/90"
              style={{ top: `${(thresholdY / height) * 100}%` }}
            >
              {Math.round(threshold ?? 0)}%
            </span>
          ) : null}
          {/* x-axis: absolute clock time of the oldest sample -> the trip */}
          <div className="mt-1 flex justify-between text-[11px] leading-none tabular-nums text-muted-foreground">
            <span data-testid="performance-warning-x-start">{startLabel}</span>
            <span data-testid="performance-warning-x-end">{endLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PerformanceWarningPayload {
  metric?: 'cpu' | 'memory';
  value?: number;
  threshold?: number;
  limit?: number;
  windowSeconds?: number;
  message?: string;
  series?: number[];
  processes?: SandboxProcessSample[];
}

function parsePerformanceWarning(item: AgentLifecycleItem) {
  const payload = (item.sourceEvent?.payload ?? null) as PerformanceWarningPayload | null;
  const metric = payload?.metric === 'memory' ? 'memory' : 'cpu';
  const metricLabel = metric === 'memory' ? 'memory' : 'CPU';
  return {
    payload,
    metric,
    metricLabel,
    message: payload?.message ?? `${metricLabel} usage stayed high`,
    series: Array.isArray(payload?.series) ? payload.series : [],
    processes: Array.isArray(payload?.processes) ? payload.processes : [],
  } as const;
}

/**
 * Sparkline + process breakdown + "View metrics" affordance for a single
 * performance warning. Shared by the standalone card and the grouped rows so
 * an expanded row carries the exact same detail as a full card.
 */
function PerformanceWarningBody({ item, agentId }: { item: AgentLifecycleItem; agentId: string }) {
  const { payload, metric, metricLabel, series, processes } = parsePerformanceWarning(item);

  // Ask the host (the agent header's sandbox badge) to open the sandbox
  // monitor and flash the chart for the metric that tripped the warning.
  // We can't reach the host's panel state across the plugin boundary, so we
  // mirror the existing `reflex:request-agent-tab` bridge: fire a window
  // CustomEvent the host listens for.
  const openMonitor = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('reflex:open-sandbox-monitor', {
        detail: { agentId, metric },
      }),
    );
  }, [agentId, metric]);

  const [processesOpen, setProcessesOpen] = useState(false);

  return (
    <>
      <WarningSparkline
        series={series}
        threshold={payload?.threshold}
        windowSeconds={payload?.windowSeconds}
        endTime={item.timestamp}
      />
      {processes.length > 0 ? (
        <Collapsible
          open={processesOpen}
          onOpenChange={setProcessesOpen}
          className="mt-3 border-t border-border/40 pt-2"
          data-testid="performance-warning-processes"
        >
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              data-testid="performance-warning-processes-toggle"
              aria-expanded={processesOpen}
              className="h-auto w-full justify-between gap-2 px-1 py-1 text-xs font-medium text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:bg-transparent"
            >
              <span className="flex items-center gap-1.5">
                Top processes
                <span className="text-[10px] tabular-nums text-muted-foreground/50">
                  {processes.length}
                </span>
              </span>
              <ChevronDown
                className={cn('size-3.5 transition-transform', processesOpen && 'rotate-180')}
                aria-hidden
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden pt-1 data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
            <ProcessUsageList
              processes={processes}
              sortBy={metric}
              memTotalBytes={metric === 'memory' ? payload?.limit : undefined}
              cpuCores={metric === 'cpu' ? payload?.limit : undefined}
              limit={5}
              data-testid="performance-warning-processes-list"
            />
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      <Button
        variant="ghost"
        onClick={openMonitor}
        data-testid="performance-warning-open-monitor"
        className="mt-3 h-auto gap-1.5 rounded-md border border-status-warning/30 bg-status-warning/10 px-3 py-1.5 text-xs font-medium text-status-warning hover:bg-status-warning/20 hover:text-status-warning dark:hover:bg-status-warning/20"
      >
        View {metricLabel} metrics
        <ArrowRight className="size-3" />
      </Button>
    </>
  );
}

/** Sizing guidance shown once per warning card / group, with the docs link. */
function PerformanceWarningSizingHint() {
  return (
    <p
      className="mt-1 text-xs leading-relaxed text-muted-foreground"
      data-testid="performance-warning-sizing-hint"
    >
      This may be a brief spike. If warnings keep appearing, a larger sandbox gives the agent more
      CPU and memory headroom.
      <InlineDocsHint slug="concepts/devboxes#performance-warnings" className="ml-1 align-middle" />
    </p>
  );
}

/**
 * One compact, expandable line for a single performance warning. Collapsed it
 * is a single row (message + peak value + time); expanded it reveals the same
 * sparkline / process / metrics detail as the old full-size card. Used both
 * as the rows of a `PerformanceWarningGroup` and, wrapped in its own bordered
 * container, as the standalone card — alerts stay one line tall in the chat
 * until the reader asks for the detail.
 */
function PerformanceWarningRow({
  item,
  agentId,
  showHint = false,
  showTooltip = false,
  'data-testid': testId = 'performance-warning-group-row',
}: {
  item: AgentLifecycleItem;
  agentId: string;
  /** Include the sizing guidance in the expanded detail (standalone card only —
   * a group shows the hint once at group level instead). */
  showHint?: boolean;
  /** Reveal the raw-event tooltip on container hover (standalone card only —
   * grouped rows defer to the group header's tooltip). */
  showTooltip?: boolean;
  'data-testid'?: string;
}) {
  const [open, setOpen] = useState(false);
  const { payload, metricLabel, message } = parsePerformanceWarning(item);
  const peak = typeof payload?.value === 'number' ? Math.round(payload.value) : null;
  const time = new Date(item.timestamp).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div data-event-id={item.sourceEvent?.id} data-testid={testId}>
      <div className="flex items-center">
        <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="performance-warning-row-toggle"
          className="h-auto min-w-0 flex-1 justify-start gap-2 rounded-none px-3 py-1.5 text-xs font-normal hover:bg-status-warning/5 dark:hover:bg-status-warning/5"
        >
          <AlertTriangle className="size-3 shrink-0 text-status-warning" />
          <span className="min-w-0 truncate font-medium text-foreground">{message}</span>
          {peak !== null && (
            <span className="shrink-0 tabular-nums text-status-warning">
              {metricLabel} {peak}%
            </span>
          )}
          <span className="ml-auto shrink-0 tabular-nums text-2xs text-muted-foreground">
            {time}
          </span>
          <ChevronDown
            className={cn(
              'size-3 shrink-0 text-muted-foreground/60 transition-transform',
              !open && '-rotate-90',
            )}
            aria-hidden
          />
        </Button>
        {showTooltip && item.sourceEvent && (
          <div className="px-2 opacity-0 transition-opacity group-hover/perf:opacity-100">
            <EventDetailTooltip event={item.sourceEvent} />
          </div>
        )}
      </div>
      <AnimatedCollapse open={open}>
        <div className="px-3 pb-3 pl-8">
          {showHint && <PerformanceWarningSizingHint />}
          <PerformanceWarningBody item={item} agentId={agentId} />
        </div>
      </AnimatedCollapse>
    </div>
  );
}

/**
 * A lone performance warning: the same compact expandable row as grouped
 * alerts, in its own amber container so it still reads as a warning at a
 * glance while staying one line tall until expanded.
 */
function PerformanceWarningCard({ item, agentId }: { item: AgentLifecycleItem; agentId: string }) {
  return (
    <div
      className="group/perf overflow-hidden rounded-md border border-status-warning/30 bg-status-warning/5"
      data-testid="performance-warning-card"
    >
      <PerformanceWarningRow
        item={item}
        agentId={agentId}
        showHint
        showTooltip
        data-testid="performance-warning-single"
      />
    </div>
  );
}

/**
 * Renders a run of consecutive performance warnings as one stacked, collapsible
 * card instead of a full-size card per warning. A sustained resource problem
 * can emit warnings every couple of minutes; individually they drown the
 * conversation, grouped they read as one incident with N data points. Closed
 * by default — one header line in the chat; expanding reveals a compact row
 * per warning, each of which expands to the full detail.
 */
function PerformanceWarningGroup({
  items,
  agentId,
}: {
  items: AgentLifecycleItem[];
  agentId: string;
}) {
  const [open, setOpen] = useState(false);
  const leader = items[0];
  const last = items[items.length - 1];
  const time = new Date(last.timestamp).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div
      className="group/perf overflow-hidden rounded-md border border-status-warning/30 bg-status-warning/5"
      data-event-id={leader.sourceEvent?.id}
      data-testid="performance-warning-group"
    >
      <div className="flex items-center">
        <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="performance-warning-group-toggle"
          className="h-auto flex-1 justify-start gap-1.5 rounded-none px-3 py-1.5 text-2xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:bg-transparent"
        >
          <AlertTriangle className="size-3 shrink-0 text-status-warning" />
          <span className="font-medium">Agent Sandbox Performance Alerts</span>
          <span className="tabular-nums text-muted-foreground/60" data-testid="perf-group-count">
            {items.length}
          </span>
          <span className="ml-auto tabular-nums text-muted-foreground/60">{time}</span>
          <ChevronDown
            className={cn(
              'size-3 shrink-0 text-muted-foreground/60 transition-transform',
              !open && '-rotate-90',
            )}
          />
        </Button>
        {last.sourceEvent && (
          <div className="px-2 opacity-0 transition-opacity group-hover/perf:opacity-100">
            <EventDetailTooltip event={last.sourceEvent} />
          </div>
        )}
      </div>
      <AnimatedCollapse open={open}>
        <div className="border-t border-status-warning/20">
          {items.map((item) => (
            <PerformanceWarningRow key={item.id} item={item} agentId={agentId} />
          ))}
          <div className="border-t border-status-warning/20 px-3 pb-2 pt-1">
            <PerformanceWarningSizingHint />
          </div>
        </div>
      </AnimatedCollapse>
    </div>
  );
}

/** Names the model with `getModelDisplayName` so the divider reads like the turn footers. */
function ModelSwitchBanner({ item }: { item: AgentLifecycleItem }) {
  const label = item.toModel ? `Switched to ${getModelDisplayName(item.toModel)}` : item.label;
  return <AgentLifecycleBanner item={{ ...item, label }} />;
}

function AgentLifecycleBanner({ item }: { item: AgentLifecycleItem }) {
  const meta = LIFECYCLE_META[item.eventType] ?? {
    icon: CircleStop,
    color: 'text-muted-foreground',
  };
  const Icon = meta.icon;
  const time = new Date(item.timestamp).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return (
    <div data-event-id={item.sourceEvent?.id}>
      <DividerBubble event={item.sourceEvent} className="py-1">
        <span className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <Icon className={cn('h-3 w-3', meta.color)} />
          {item.label}
          <span className="tabular-nums">{time}</span>
        </span>
      </DividerBubble>
    </div>
  );
}

/**
 * Find runs of consecutive lifecycle items of a given event type. Each run is
 * keyed by its leading item's id; every other item in the run lands in
 * `suppressedIds` so the transcript renders one grouped card at the leader and
 * skips the rest. Used to stack noisy repeated events (stderr log lines,
 * performance warnings) instead of scattering a full-size item per event.
 */
function collectLifecycleRuns(
  messages: readonly { id: string; role: string }[],
  eventType: string,
): { runByLeaderId: Map<string, AgentLifecycleItem[]>; suppressedIds: Set<string> } {
  const runByLeaderId = new Map<string, AgentLifecycleItem[]>();
  const suppressedIds = new Set<string>();
  const matches = (m: (typeof messages)[number]): m is AgentLifecycleItem =>
    m.role === 'system' &&
    'itemType' in m &&
    m.itemType === 'agent_lifecycle' &&
    (m as AgentLifecycleItem).eventType === eventType;
  let i = 0;
  while (i < messages.length) {
    if (!matches(messages[i])) {
      i++;
      continue;
    }
    const run: AgentLifecycleItem[] = [];
    let j = i;
    while (j < messages.length && matches(messages[j])) {
      const item = messages[j] as AgentLifecycleItem;
      run.push(item);
      if (j !== i) suppressedIds.add(item.id);
      j++;
    }
    runByLeaderId.set(run[0].id, run);
    i = j;
  }
  return { runByLeaderId, suppressedIds };
}

/**
 * Timeline row for a run of consecutive stdout/stderr log lines that landed
 * between turns. Log lines that arrive *inside* a turn are `agent_log` blocks
 * instead, rendered by the same console from within the turn's card.
 */
function AgentLogGroup({ lines }: { lines: AgentLifecycleItem[] }) {
  // `lines` is rebuilt on every render by the run grouping above, so memoizing
  // this map would allocate a dependency check that can never hit.
  const last = lines[lines.length - 1];

  return (
    <AgentLogConsole
      lines={lines.map((line) => ({
        id: line.id,
        text: line.label,
        sourceEvent: line.sourceEvent,
      }))}
      timestamp={last.timestamp}
      sourceEvent={last.sourceEvent}
      dataEventId={lines[0].sourceEvent?.id}
    />
  );
}

export function SDKChatView({
  events,
  isGenerating,
  workingLabel,
  agentId,
  agentModel,
  protocol,
  onCommandsChange,
  onActiveModelChange,
  onLaunchedAgentsChange,
  queuedMessages,
  onEditQueued,
  onDeleteQueued,
  onReorderQueued,
  onSendQueuedNow,
  pendingMessages,
  onRetryPending,
  onDismissPending,
  highlightEventId,
  onHighlightConsumed,
  pagedHistory,
  onActiveQuestionChange,
  globalHotkeys,
}: SDKChatViewProps) {
  const { messages, currentTurnBlocks, isAgentTurn, availableCommands, workingDir, activeModel } =
    useSDKStream(events, protocol);

  // Pending AskUserQuestion prompts register here so we can dim the transcript,
  // disable the composer (via onActiveQuestionChange), and lift the prompt into
  // the overlay slot below.
  const [overlayEl, setOverlayEl] = useState<HTMLDivElement | null>(null);
  const pendingRef = useRef<Set<string>>(new Set());
  const [pendingCount, setPendingCount] = useState(0);
  const setPending = useCallback((blockId: string, pending: boolean) => {
    const set = pendingRef.current;
    if (pending ? set.has(blockId) : !set.has(blockId)) return;
    if (pending) set.add(blockId);
    else set.delete(blockId);
    setPendingCount(set.size);
  }, []);
  const overlayCtx = useMemo<AskUserOverlayContextValue>(
    () => ({ enabled: true, overlayEl, setPending }),
    [overlayEl, setPending],
  );
  const hasPendingQuestion = pendingCount > 0;
  useEffect(() => {
    onActiveQuestionChange?.(hasPendingQuestion);
  }, [hasPendingQuestion, onActiveQuestionChange]);

  // Pending capability requests dock above the composer (see
  // CapabilityDockContext); answered ones stay inline in the thread.
  const [capabilityDockEl, setCapabilityDockEl] = useState<HTMLDivElement | null>(null);
  const capabilityDockCtx = useMemo<CapabilityDockContextValue>(
    () => ({ enabled: true, dockEl: capabilityDockEl }),
    [capabilityDockEl],
  );

  const { containerRef, contentRef, showScrollBtn, onScrollBtnClick, releasePin } = useAutoScroll({
    initiallyPinned: highlightEventId ? false : undefined,
    storageKey: `agent-chat:${agentId}`,
  });
  // The turn rail only mounts when the chat pane is wide enough to give it a
  // gutter — on a phone, or with the detail panel open on a laptop, there is
  // none and the transcript is the whole view.
  const chatPaneRef = useRef<HTMLDivElement>(null);
  // `contentRef` is the transcript column itself, so the rail is answered by
  // the column as drawn rather than by the width preference behind it.
  const roomForRail = useHasRoomForRail(chatPaneRef, contentRef);
  const railRef = useRef<SectionNavHandle | null>(null);
  // Wanting the rail and having room for it are separate questions: the
  // toolbar's "Turn menu" switch says whether it is wanted at all, and a narrow
  // pane hides it either way.
  const [turnMenuWanted] = useTurnMenuVisible();
  const showTurnNav = turnMenuWanted && roomForRail;
  const connectivity = useAgentConnectivity(agentId);
  const consumedHighlightRef = useRef<string | null>(null);

  // Only the newest slice of a finalized transcript is reachable initially;
  // measured turn spacers below keep its eventual DOM mount bounded. Reset
  // when the view is pointed at another agent, which does not always remount it.
  const [requestedVisible, setRequestedVisible] = useState(INITIAL_VISIBLE_MESSAGES);
  useEffect(() => {
    setRequestedVisible(INITIAL_VISIBLE_MESSAGES);
    // A pending prepend anchor belongs to the previous agent's transcript.
    prependAnchorRef.current = null;
  }, [agentId]);
  const { visible: visibleMessages, hiddenCount } = useMemo(
    () => resolveTranscriptWindow(messages, requestedVisible),
    [messages, requestedVisible],
  );
  const virtualTurnHeights = useMemo(() => new Map<string, VirtualTurnMeasurement>(), [agentId]);
  const virtualTurnGroups = useMemo(() => groupTranscriptTurns(visibleMessages), [visibleMessages]);

  // The turn rail's index-first mode: the whole session's prompt rows plus
  // the jump machinery, memoized so the rail's own memos see a stable object
  // between renders that changed neither.
  const indexTurns = pagedHistory?.indexTurns;
  const onJumpToTurn = pagedHistory?.onJumpToTurn;
  const jumpPendingSeq = pagedHistory?.jumpPendingSeq;
  const sessionIndexTurns = useMemo(
    () =>
      indexTurns && indexTurns.length > 0 && onJumpToTurn
        ? { turns: indexTurns, onSelectUnloaded: onJumpToTurn, loadingSeq: jumpPendingSeq ?? null }
        : undefined,
    [indexTurns, onJumpToTurn, jumpPendingSeq],
  );

  // Distance from the bottom of the scroll content, captured before older
  // items mount so the reader's viewport can be restored afterwards. Without
  // it, prepending history pushes whatever they were reading off-screen.
  // Stepping through turns from the keyboard. Only on the live surface, and it
  // stands down while a prompt is open — the prompt's own question stepper
  // takes the same keys.
  const onBeforeStep = useCallback(
    (targetId: string) => {
      releasePin();
      // The keys scroll the transcript themselves, so the rail is told where
      // they went. Left to infer it from the scroll, it would keep highlighting
      // the end of the chat (the jump usually lands inside the near-the-bottom
      // window) or the turn below the one stepped to, which sits above the
      // band the observer watches.
      railRef.current?.pin(targetId);
    },
    [releasePin],
  );
  useTurnStepping({
    containerRef,
    messages: visibleMessages,
    enabled: Boolean(globalHotkeys) && !hasPendingQuestion,
    onBeforeStep,
  });

  // Must be called before the reveal-restore layout effect below: React runs
  // layout effects in hook-call order, and the restore's
  // `scrollHeight - scrollTop` arithmetic is only right once the freshly
  // mounted rows carry their measured heights.
  useMeasuredRowSizes(contentRef, virtualTurnGroups);

  const revealAnchorRef = useRef<number | null>(null);
  const revealEarlier = useCallback(() => {
    const container = containerRef.current;
    revealAnchorRef.current = container ? container.scrollHeight - container.scrollTop : null;
    const next = messages.length - hiddenCount + REVEAL_STEP;
    // Reaching the start of the thread makes the reveal permanent for this
    // agent. Pinning to a count instead would re-hide the oldest item the
    // moment the next message landed, so a reader who asked for the whole
    // transcript would watch the button reappear behind their back.
    setRequestedVisible(next >= messages.length ? Number.POSITIVE_INFINITY : next);
  }, [containerRef, hiddenCount, messages.length]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const anchor = revealAnchorRef.current;
    if (!container || anchor === null) return;
    revealAnchorRef.current = null;
    container.scrollTop = container.scrollHeight - anchor;
  }, [visibleMessages, containerRef]);

  // Continuous scroll: viewport anchoring for network prepends. At trigger
  // time (sentinel contact, or a load-earlier pill click) the topmost
  // rendered event element and its offset within the content box are
  // captured; when the prepend lands — detected as the topmost event id
  // changing, which a live append at the bottom never does — the same
  // element is found again and the scroll moved by exactly how far down
  // the content it was pushed. Measuring against the content box, not the
  // viewport, keeps the reader's own scrolling between contact and landing
  // out of the correction: with the lookahead margin, contact happens
  // while they are still moving. Anchoring by stable event id rather than
  // node identity survives a rebuild-and-remount of the transcript (a
  // middle backfill replays the fold from event zero).
  const prependAnchorRef = useRef<{ id: string; offset: number } | null>(null);
  const capturePrependAnchor = useCallback(() => {
    const content = contentRef.current;
    const el = content?.querySelector<HTMLElement>('[data-event-id]');
    const id = el?.getAttribute('data-event-id');
    prependAnchorRef.current =
      content && el && id ? { id, offset: contentOffset(content, el) } : null;
  }, [contentRef]);
  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    const anchor = prependAnchorRef.current;
    if (!container || !content || !anchor) return;
    const first = content.querySelector<HTMLElement>('[data-event-id]');
    // Topmost id unchanged: nothing prepended yet (the fallback fetch is
    // still in flight) — keep the anchor armed for the render that does.
    if (!first || first.getAttribute('data-event-id') === anchor.id) return;
    prependAnchorRef.current = null;
    const el = content.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(anchor.id)}"]`);
    if (!el) return;
    container.scrollTop += contentOffset(content, el) - anchor.offset;
  }, [visibleMessages, containerRef, contentRef]);

  // The continuous-scroll sentinel: an invisible line above the topmost
  // rendered content, observed with a lookahead margin so contact happens
  // while the reader is still screens away from the edge. Contact drains
  // the staged buffer above the window (or, with an empty buffer — the
  // fast-scroll degenerate case — falls back to the bigger-window
  // page-back), anchored so the viewport never jumps. The observer is
  // rebuilt whenever the pager's commit identity changes (the buffer
  // refilled), and a fresh observer reports the current intersection, so
  // a sentinel still inside the margin keeps draining until the rendered
  // history runs past the lookahead. Only mounted once the DOM window is
  // fully revealed and only when the data layer wired a pager; legacy
  // agents and flag-off sessions render nothing.
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const commitStagedTurn = pagedHistory?.commitStagedTurn;
  const onLoadEarlier = pagedHistory?.onLoadEarlier;
  const isLoadingEarlier = pagedHistory?.isLoadingEarlier ?? false;
  const sentinelActive = Boolean(commitStagedTurn && pagedHistory?.hasMore && hiddenCount === 0);
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = containerRef.current;
    if (!sentinelActive || !sentinel || !root || !commitStagedTurn) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        capturePrependAnchor();
        // The reader is at the very top of the loaded transcript with more
        // arriving above, so the reachable window becomes permanent — same
        // rule as revealEarlier reaching the start. Distant completed turns
        // can still collapse into measured spacers.
        setRequestedVisible(Number.POSITIVE_INFINITY);
        // `pending` waits (the pager commits the in-flight turn when it
        // lands); only a true miss falls back to the page-back.
        if (commitStagedTurn() === 'miss' && !isLoadingEarlier) onLoadEarlier?.();
      },
      { root, rootMargin: `${CONTINUOUS_SCROLL_LOOKAHEAD_PX}px 0px 0px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    sentinelActive,
    commitStagedTurn,
    onLoadEarlier,
    isLoadingEarlier,
    capturePrependAnchor,
    containerRef,
  ]);

  useEffect(() => {
    onCommandsChange?.(availableCommands);
  }, [availableCommands, onCommandsChange]);

  useEffect(() => {
    onActiveModelChange?.(activeModel);
  }, [activeModel, onActiveModelChange]);

  useEffect(() => {
    if (!highlightEventId) return;
    if (consumedHighlightRef.current === highlightEventId) return;

    const container = containerRef.current;
    if (!container) return;

    const timer = setTimeout(() => {
      const el = container.querySelector<HTMLElement>(
        `[data-event-id="${CSS.escape(highlightEventId)}"]`,
      );
      if (!el) {
        // A deep link (the Axon view's "show in chat", a shared `?eventId=`
        // URL) can point at an event older than the window. Mount the whole
        // transcript and let this effect run again to scroll to it.
        if (hiddenCount > 0) setRequestedVisible(Number.POSITIVE_INFINITY);
        return;
      }
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('animate-highlight-fade');
      const cleanup = window.setTimeout(() => {
        el.classList.remove('animate-highlight-fade');
      }, 1500);
      consumedHighlightRef.current = highlightEventId;
      onHighlightConsumed?.();
      return () => window.clearTimeout(cleanup);
    }, 100);
    return () => clearTimeout(timer);
  }, [highlightEventId, messages, containerRef, onHighlightConsumed, hiddenCount]);

  // Show a waiting indicator (avatar + "Working…") when the agent is working
  // but has no in-flight turn yet — e.g. right after the user sends a message
  // and before the first assistant block arrives. Suppressed during setup,
  // since the setup card already conveys progress.
  const isSetupInProgress = messages.some(
    (m): m is SetupProgressItem =>
      m.role === 'system' && 'itemType' in m && m.itemType === 'setup_progress' && !m.isComplete,
  );
  const showWaitingIndicator =
    isGenerating && !isAgentTurn && currentTurnBlocks.length === 0 && !isSetupInProgress;

  // Collapse consecutive stdout/stderr log lines into a single grouped console
  // card. We render the group at its leading line and suppress the rest so the
  // transcript shows one tidy "Logs" block instead of a scattered run of `>_`
  // lines with full inter-message spacing between each.
  // Runs are collected over the mounted window, not the whole transcript: a
  // run split by the window boundary has to regroup around its oldest mounted
  // line, or the followers would be suppressed with no leader to render them.
  const { runByLeaderId: logRunByLeaderId, suppressedIds: suppressedLogIds } = useMemo(
    () => collectLifecycleRuns(visibleMessages, 'agent.stderr'),
    [visibleMessages],
  );

  // Same treatment for performance warnings: a sustained resource problem can
  // fire one warning after another, and a full card per warning drowns the
  // conversation. Consecutive warnings render as one stacked group at the
  // leading item; the rest are suppressed.
  const { runByLeaderId: perfRunByLeaderId, suppressedIds: suppressedPerfIds } = useMemo(
    () => collectLifecycleRuns(visibleMessages, 'agent.performance_warning'),
    [visibleMessages],
  );

  // A capability card is answered in place: the later `capability_resolved`
  // event folds into the request card it names and renders nothing of its
  // own, so a request answered from the dock, another tab, or Slack reads
  // the same as one answered here. Resolutions are collected over the whole
  // transcript, not the window: the request can sit pages above its answer.
  const capabilityResolutions = useMemo(() => {
    const byRequestId = new Map<string, AgentCapabilityResolvedPayload>();
    for (const m of messages) {
      if (m.role !== 'system' || !('eventType' in m)) continue;
      const lifecycle = m as AgentLifecycleItem;
      if (lifecycle.eventType !== 'agent.capability_resolved') continue;
      const parsed = AgentCapabilityResolvedPayloadSchema.safeParse(lifecycle.sourceEvent?.payload);
      if (parsed.success) byRequestId.set(parsed.data.requestId, parsed.data);
    }
    return byRequestId;
  }, [messages]);

  // Surface agents this agent launched through Reflex MCP in the host's
  // activity bar, derived from the finalized assistant blocks plus the live
  // turn. The launch/result tool calls stay visible as regular tool-call rows
  // in the transcript; the bar is the launched-agent affordance.
  const launchedAgentCards = useMemo(() => {
    const blocks: TurnBlock[] = [];
    for (const msg of messages) {
      if (msg.role === 'assistant' && 'blocks' in msg && msg.blocks) blocks.push(...msg.blocks);
    }
    blocks.push(...currentTurnBlocks);
    return parseLaunchedAgents(blocks);
  }, [messages, currentTurnBlocks]);

  // Lift the launched agents into the host's activity bar (mirrors
  // onActiveModelChange; ordering and emit dedupe live in the hook).
  useLaunchedAgentsEmitter(launchedAgentCards, onLaunchedAgentsChange);

  // A harness announces session metadata once, on the first assistant turn.
  // Remember the newest announcement for every later response so each avatar
  // opens the same session details, including when transcript windowing has
  // unmounted the response that originally carried the init block.
  const { sessionInitByMessageId, currentSessionInit } = useMemo(() => {
    const byMessageId = new Map<string, SystemInitBlock>();
    let latest: SystemInitBlock | undefined;

    for (const message of messages) {
      if (message.role !== 'assistant' || !('blocks' in message)) continue;
      latest =
        message.blocks?.find((block): block is SystemInitBlock => block.type === 'system_init') ??
        latest;
      if (latest) byMessageId.set(message.id, latest);
    }

    const liveInit = currentTurnBlocks.find(
      (block): block is SystemInitBlock => block.type === 'system_init',
    );
    return {
      sessionInitByMessageId: byMessageId,
      currentSessionInit: liveInit ?? latest,
    };
  }, [messages, currentTurnBlocks]);

  return (
    <TranscriptScope agentId={agentId} workingDir={workingDir}>
      <AskUserOverlayContext.Provider value={overlayCtx}>
        <CapabilityDockContext.Provider value={capabilityDockCtx}>
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* min-h keeps a sliver of transcript visible when a tall docked
              AskUserQuestion card below claims the rest of the column. The
              floor only bites once the column runs out of room, so while a
              prompt is pending it drops to a single line: on a phone with the
              keyboard up, 96px of dimmed transcript is 96px the card needs to
              keep its options and its submit button reachable. */}
            <div
              ref={chatPaneRef}
              className={cn(
                '@container relative flex-1 overflow-hidden',
                hasPendingQuestion ? 'min-h-8' : 'min-h-24',
              )}
            >
              <div
                ref={containerRef}
                // The prepend anchoring above is the only scroll correction:
                // the browser's own anchoring (which only stands down at
                // scrollTop 0) would stack a second correction on every
                // lookahead prepend that lands mid-scroll.
                className="h-full overflow-y-auto [overflow-anchor:none]"
                role="log"
                aria-label="Messages"
                data-tutorial-id="agent-chat-log"
              >
                <div
                  ref={contentRef}
                  // The transcript has three cost controls: an initial reachable
                  // window, measured turn wrappers that unmount distant heavy
                  // subtrees behind exact-height spacers, and
                  // `content-visibility: auto` to skip style/layout/paint for the
                  // warm overscan neighborhood. Pending questions stay mounted;
                  // deep links force their owning turn warm before scrolling.
                  // The CSS applies to every direct child, covering finalized
                  // turns, controls, pending bubbles, and trailing indicators.
                  //
                  // Each row's `contain-intrinsic-size` is pinned to its
                  // measured height by `useMeasuredRowSizes`, so skipping and
                  // unskipping a row changes no layout — a shared estimate here
                  // made the content above a reader resize as they scrolled
                  // into it. `useAutoScroll` restores position by element
                  // anchor, so sizing never corrupts scroll restoration.
                  data-testid="chat-transcript-column"
                  className={cn(
                    CHAT_COLUMN_CLASS,
                    'flex flex-col gap-5 py-6',
                    '[&>*]:[content-visibility:auto]',
                  )}
                >
                  {/* Start-of-mounted-transcript target for the turn rail's
                    "Earlier turns" jump. Zero placeholder height for the same
                    reason as the end sentinel below. */}
                  <div
                    id={CHAT_START_ANCHOR_ID}
                    tabIndex={-1}
                    aria-hidden
                    className="h-px outline-none"
                    style={{ containIntrinsicSize: '0px' }}
                  />

                  {hiddenCount > 0 && (
                    <div className="flex justify-center">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="gap-1.5 rounded-full text-xs"
                        onClick={revealEarlier}
                      >
                        Show earlier messages
                        <span className="text-muted-foreground tabular-nums">
                          {hiddenCount.toLocaleString()} hidden
                        </span>
                      </Button>
                    </div>
                  )}

                  {/* Contact with this line commits the staged previous turn
                    (see the observer above). Zero height so it costs no
                    layout; the pill below stays as the visible affordance
                    and the no-observer fallback. */}
                  {sentinelActive && (
                    <div
                      ref={topSentinelRef}
                      data-testid="chat-scroll-sentinel"
                      aria-hidden
                      className="h-px"
                      style={{ containIntrinsicSize: '0px' }}
                    />
                  )}

                  {/* Older history exists on the server but not in memory: the
                    network counterpart of "Show earlier messages" above. Only
                    once every loaded row is revealed — the DOM window opens
                    first, then paging reaches below it. */}
                  {hiddenCount === 0 && pagedHistory?.hasMore && (
                    <div className="flex justify-center">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="gap-1.5 rounded-full text-xs"
                        onClick={() => {
                          // The click prepends too, so it gets the same
                          // viewport anchoring as the sentinel path.
                          capturePrependAnchor();
                          pagedHistory.onLoadEarlier();
                        }}
                        disabled={pagedHistory.isLoadingEarlier}
                        data-testid="chat-load-earlier"
                      >
                        {pagedHistory.isLoadingEarlier && (
                          <Loader2 className="size-3 animate-spin" aria-hidden />
                        )}
                        Load earlier messages
                      </Button>
                    </div>
                  )}

                  {virtualTurnGroups.map((group) => (
                    <MeasuredVirtualTurn
                      key={`${agentId}:${group.id}`}
                      group={group}
                      rootRef={containerRef}
                      heightCache={virtualTurnHeights}
                      forceMounted={Boolean(
                        highlightEventId && group.eventIds.includes(highlightEventId),
                      )}
                      render={() =>
                        group.items.map((msg) => {
                          if (
                            msg.role === 'system' &&
                            'itemType' in msg &&
                            msg.itemType === 'codex_command_result'
                          ) {
                            return (
                              <AnimatedChatItem key={msg.id}>
                                <CodexCommandResultBlockView
                                  block={(msg as CodexCommandResultItem).block}
                                />
                              </AnimatedChatItem>
                            );
                          }
                          if (
                            msg.role === 'system' &&
                            'itemType' in msg &&
                            msg.itemType === 'setup_progress'
                          ) {
                            const setup = msg as SetupProgressItem;
                            return (
                              <AnimatedChatItem key={msg.id}>
                                <AgentSetupProgress
                                  setupEvents={setup.setupEvents}
                                  isComplete={setup.isComplete}
                                  agentId={agentId}
                                />
                              </AnimatedChatItem>
                            );
                          }
                          if (
                            msg.role === 'system' &&
                            'itemType' in msg &&
                            msg.itemType === 'agent_started'
                          ) {
                            return (
                              <AnimatedChatItem key={msg.id}>
                                <AgentConfigBanner item={msg as AgentConfigItem} />
                              </AnimatedChatItem>
                            );
                          }
                          if (
                            msg.role === 'system' &&
                            'itemType' in msg &&
                            msg.itemType === 'agent_lifecycle'
                          ) {
                            const lifecycle = msg as AgentLifecycleItem;
                            if (lifecycle.eventType === 'agent.stderr') {
                              if (suppressedLogIds.has(msg.id)) return null;
                              const run = logRunByLeaderId.get(msg.id) ?? [lifecycle];
                              return <AgentLogGroup key={msg.id} lines={run} />;
                            }
                            if (
                              lifecycle.eventType === 'agent.error' ||
                              lifecycle.eventType === 'devbox.failed'
                            ) {
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <AgentErrorCard item={lifecycle} />
                                </AnimatedChatItem>
                              );
                            }
                            if (lifecycle.eventType === 'agent.source.flow') {
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <FlowSourceCard item={lifecycle} />
                                </AnimatedChatItem>
                              );
                            }
                            if (lifecycle.eventType === 'agent.flow_result') {
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <FlowResultCard item={lifecycle} />
                                </AnimatedChatItem>
                              );
                            }
                            if (lifecycle.eventType === 'agent.capability_resolved') return null;
                            if (lifecycle.eventType === 'agent.capability_requested') {
                              const requestId = parseCapabilityRequest(lifecycle)?.requestId;
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <CapabilityRequestCard
                                    item={lifecycle}
                                    agentId={agentId}
                                    resolution={
                                      requestId ? capabilityResolutions.get(requestId) : null
                                    }
                                  />
                                </AnimatedChatItem>
                              );
                            }
                            if (lifecycle.eventType === 'agent.runtime_settings_changed') {
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <ModelSwitchBanner item={lifecycle} />
                                </AnimatedChatItem>
                              );
                            }
                            if (lifecycle.eventType === 'agent.daemon_started') {
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <DaemonStartedCard item={lifecycle} />
                                </AnimatedChatItem>
                              );
                            }
                            if (lifecycle.eventType === 'agent.work_assessment') {
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <WorkAssessmentCard item={lifecycle} agentId={agentId} />
                                </AnimatedChatItem>
                              );
                            }
                            if (lifecycle.eventType === 'agent.performance_warning') {
                              if (suppressedPerfIds.has(msg.id)) return null;
                              const run = perfRunByLeaderId.get(msg.id) ?? [lifecycle];
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  {run.length > 1 ? (
                                    <PerformanceWarningGroup items={run} agentId={agentId} />
                                  ) : (
                                    <PerformanceWarningCard item={lifecycle} agentId={agentId} />
                                  )}
                                </AnimatedChatItem>
                              );
                            }
                            if (
                              lifecycle.eventType === 'agent.pr_created' ||
                              lifecycle.eventType === 'agent.pr_merged' ||
                              lifecycle.eventType === 'agent.pr_closed' ||
                              lifecycle.eventType === 'agent.pr_checks_failed' ||
                              lifecycle.eventType === 'agent.pr_review'
                            ) {
                              return (
                                <AnimatedChatItem key={msg.id}>
                                  <AgentPrEventCard item={lifecycle} />
                                </AnimatedChatItem>
                              );
                            }
                            return (
                              <AnimatedChatItem key={msg.id}>
                                <AgentLifecycleBanner item={lifecycle} />
                              </AnimatedChatItem>
                            );
                          }
                          if (msg.role === 'user') {
                            return (
                              <AnimatedChatItem key={msg.id}>
                                <UserMessage
                                  content={msg.content}
                                  attachments={'attachments' in msg ? msg.attachments : undefined}
                                  sourceEvent={'sourceEvent' in msg ? msg.sourceEvent : undefined}
                                />
                              </AnimatedChatItem>
                            );
                          }
                          if (msg.role === 'assistant' && 'blocks' in msg) {
                            return (
                              <AnimatedChatItem key={msg.id}>
                                <AssistantTurn
                                  blocks={msg.blocks ?? []}
                                  sessionInitBlock={sessionInitByMessageId.get(msg.id)}
                                  isLive={false}
                                  stopReason={msg.stopReason}
                                  isError={msg.isError}
                                  cost={msg.cost}
                                  tokenUsage={msg.tokenUsage}
                                  numTurns={msg.numTurns}
                                  durationMs={msg.durationMs}
                                  timestamp={msg.timestamp}
                                  sourceEvent={msg.sourceEvent}
                                  agentId={agentId}
                                  highlightEventId={
                                    highlightEventId && group.eventIds.includes(highlightEventId)
                                      ? highlightEventId
                                      : undefined
                                  }
                                  agentType={protocol}
                                  agentModel={msg.model ?? agentModel}
                                />
                              </AnimatedChatItem>
                            );
                          }
                          return null;
                        })
                      }
                    />
                  ))}

                  {pendingMessages?.map((pm) => {
                    const previewAttachments: UserAttachment[] | undefined =
                      pm.attachments?.flatMap((a): UserAttachment[] => {
                        if (!a.mimeType) return [];
                        if (a.mimeType.startsWith('image/') && a.data) {
                          return [
                            { type: 'image', data: a.data, mimeType: a.mimeType, name: a.name },
                          ];
                        }
                        return [
                          {
                            type: 'file',
                            name: a.name,
                            mimeType: a.mimeType,
                            ...(a.data ? { data: a.data } : {}),
                          },
                        ];
                      });
                    const showActions =
                      !pm.synthetic && (pm.status === 'failed' || pm.status === 'unconfirmed');
                    return (
                      <AnimatedChatItem key={`pending-${pm.clientId}`}>
                        <UserMessage
                          content={pm.text}
                          attachments={previewAttachments?.length ? previewAttachments : undefined}
                          isPending={pm.status === 'sending'}
                          isFailed={pm.status === 'failed'}
                          isUnconfirmed={pm.status === 'unconfirmed'}
                          errorMessage={pm.error}
                          senderUserId={pm.userId}
                          onRetry={
                            showActions && onRetryPending
                              ? () => onRetryPending(pm.clientId)
                              : undefined
                          }
                          onDismiss={
                            showActions && onDismissPending
                              ? () => onDismissPending(pm.clientId)
                              : undefined
                          }
                        />
                      </AnimatedChatItem>
                    );
                  })}

                  {/* Mount the live turn card as soon as the turn starts, even with
                    no blocks yet — Codex opts out of streaming deltas, so its
                    first renderable frame can arrive seconds into the turn. The
                    card's empty-body state renders the working indicator. */}
                  {(currentTurnBlocks.length > 0 || isAgentTurn) && (
                    <AnimatedChatItem>
                      <AssistantTurn
                        blocks={currentTurnBlocks}
                        sessionInitBlock={currentSessionInit}
                        isLive={isAgentTurn}
                        workingLabel={workingLabel}
                        agentId={agentId}
                        highlightEventId={highlightEventId}
                        agentType={protocol}
                        agentModel={activeModel ?? agentModel}
                      />
                    </AnimatedChatItem>
                  )}

                  {showWaitingIndicator && (
                    <AnimatedChatItem>
                      <WorkingIndicator visible label={workingLabel} />
                    </AnimatedChatItem>
                  )}

                  {/* End-of-chat target for the turn rail. Both sentinels pin
                    their placeholder height to zero: they are direct children,
                    so they are skipped like any other row, and a ghost gap at
                    the end of the transcript would fight the pinned-to-bottom
                    scroll. */}
                  <div
                    id={CHAT_END_ANCHOR_ID}
                    tabIndex={-1}
                    aria-hidden
                    className="h-px outline-none"
                    style={{ containIntrinsicSize: '0px' }}
                  />
                </div>
              </div>

              {/* The rail floats in the right gutter instead of taking a column
                of its own: the transcript keeps the same width whether or not
                it is showing, so appearing costs no reflow and the row-height
                estimate above stays tuned for one column width. Only the rail
                itself takes pointer events, so a wheel or a swipe over the
                empty gutter still scrolls the transcript. It widens where the
                gutter has room: rows carry a number, a status glyph and
                sometimes an image count, and at the minimum width the prompt
                itself gets squeezed. */}
              {showTurnNav && (
                <aside className="pointer-events-none absolute bottom-6 right-4 top-6 w-44 @[86rem]:w-56">
                  <ChatTurnNav
                    ref={railRef}
                    mountedMessages={visibleMessages}
                    liveBlocks={currentTurnBlocks}
                    isGenerating={isAgentTurn || isGenerating}
                    scrollRootRef={containerRef}
                    hasEarlierMessages={hiddenCount > 0}
                    sessionIndexTurns={sessionIndexTurns}
                    onSelect={releasePin}
                    showHotkeyHint={globalHotkeys}
                    // Short by design: tall enough for the listed turns and their
                    // pinned ends, never a full-height column of text beside the
                    // conversation.
                    className="pointer-events-auto max-h-[min(24rem,100%)]"
                  />
                </aside>
              )}

              {hasPendingQuestion && (
                <div
                  className="pointer-events-none absolute inset-0 z-10 bg-background/25"
                  data-testid="ask-user-overlay-scrim"
                  aria-hidden
                />
              )}

              <AnimatePresence initial={false}>
                {showScrollBtn && (
                  <motion.div
                    key="scroll-btn"
                    className="absolute bottom-2 left-1/2 -translate-x-1/2"
                    initial={{ opacity: 0, transform: 'translateY(4px) scale(0.95)' }}
                    animate={{ opacity: 1, transform: 'translateY(0px) scale(1)' }}
                    exit={{
                      opacity: 0,
                      transform: 'translateY(4px) scale(0.95)',
                      transition: { duration: 0.1 },
                    }}
                    transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                  >
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-8 w-8 rounded-full shadow-md"
                      onClick={onScrollBtnClick}
                      aria-label="Scroll to bottom"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Docked queued-messages slot. Pinned directly above the composer
            (and the AskUserQuestion slot below) so queued messages hug the chat
            bar regardless of scroll position, rather than floating at the end of
            a short transcript. */}
            {queuedMessages && queuedMessages.length > 0 && (
              <div
                className={cn(CHAT_COLUMN_CLASS, 'shrink-0 pb-1')}
                data-testid="queued-messages-slot"
              >
                <AnimatedChatItem>
                  <QueuedMessageBlock
                    messages={queuedMessages}
                    onEdit={onEditQueued ?? (() => {})}
                    onDelete={onDeleteQueued ?? (() => {})}
                    onReorder={onReorderQueued}
                    onSendNow={onSendQueuedNow}
                  />
                </AnimatedChatItem>
              </div>
            )}

            {/* Docked capability-request slot. Pending approval cards portal in
            here, above the AskUserQuestion slot and the composer, so an
            approval the agent is waiting on is one glance away however far
            the transcript has scrolled. Several pending requests stack and
            scroll within a bounded height; the transcript is not dimmed and
            the composer stays live, since deciding does not block the chat. */}
            <div
              ref={setCapabilityDockEl}
              data-testid="capability-dock-slot"
              className={cn(
                CHAT_COLUMN_CLASS,
                'max-h-[45vh] shrink-0 space-y-2 overflow-y-auto empty:hidden pb-2',
              )}
            />

            {/* Docked AskUserQuestion slot. The card portals in here and sits flush
            above the composer in normal flow. It takes its natural height while
            the column has room (the transcript keeps a min-height sliver);
            when it doesn't fit, the card shrinks and scrolls its own middle
            section, keeping the header and the footer with the submit button
            visible instead of scrolling them away. */}
            <div className={cn('flex min-h-0 justify-center', hasPendingQuestion && 'px-4')}>
              <div
                ref={setOverlayEl}
                data-testid="ask-user-overlay-slot"
                className="flex min-h-0 w-full max-w-4xl flex-col"
              />
            </div>

            {connectivity.state !== 'live' && (
              <ConnectivityBanner
                state={connectivity.state}
                since={connectivity.since}
                reason={connectivity.reason}
              />
            )}
          </div>
        </CapabilityDockContext.Provider>
      </AskUserOverlayContext.Provider>
    </TranscriptScope>
  );
}
